import { useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  "Startup / SME",
  "Funded Technology Company",
  "Global Capability Centre",
  "Company Building in India",
  "Large Enterprise",
  "Other",
];

const TOPICS = [
  "Virtual & Fractional CFO",
  "Finance Shared Services & Outsourced Accounting",
  "Controllership & Reporting",
  "Compliance & Regulatory",
  "Treasury, Controls & Finance Transformation",
  "FP&A & Investor Reporting",
  "Something else",
];

const INITIAL = { name: "", company: "", email: "", phone: "", stage: "", topic: "", message: "" };

export const ContactModal = ({ open, onOpenChange }) => {
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setError("");
    try {
      await axios.post(`${API}/contact`, { ...form, phone: form.phone || null });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setError("Something went wrong while submitting. Please email us directly at ramesh@cogniticfo.com.");
    }
  };

  const close = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(() => { setStatus("idle"); setForm(INITIAL); setError(""); }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        data-testid="consultation-modal"
        className="max-h-[92vh] overflow-y-auto sm:max-w-[560px] rounded-2xl border-border p-0"
      >
        {status === "success" ? (
          <div data-testid="form-success-message" className="flex flex-col items-center px-10 py-16 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-brand/15">
              <CheckCircle2 size={30} className="text-brand-hover" />
            </span>
            <h3 className="mt-6 font-display text-2xl font-bold text-navy">Thank you, {form.name.split(" ")[0]}.</h3>
            <p className="mt-3 max-w-sm text-sm leading-relaxed text-body">
              Your enquiry has been received. We will get back to you within one
              business day to schedule your conversation with Ramesh Nanduri.
            </p>
            <button
              data-testid="form-success-close-btn"
              onClick={() => close(false)}
              className="mt-8 rounded-full bg-navy px-8 py-3 text-sm font-bold text-white transition-colors duration-300 hover:bg-navy-700"
            >
              Done
            </button>
          </div>
        ) : (
          <div className="p-8 md:p-10">
            <DialogHeader>
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-brand-hover">Book a Consultation</p>
              <DialogTitle className="mt-2 font-display text-3xl font-bold tracking-tight text-navy">
                Start the conversation.
              </DialogTitle>
              <DialogDescription className="text-sm leading-relaxed text-body">
                Tell us a little about your business. We respond within one business day.
              </DialogDescription>
            </DialogHeader>

            <form data-testid="consultation-form" onSubmit={submit} className="mt-7 space-y-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="cf-name" className="text-xs font-bold uppercase tracking-wider text-navy/70">Name *</Label>
                  <Input id="cf-name" data-testid="form-name-input" required value={form.name} onChange={set("name")} placeholder="Your full name" className="h-11" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cf-company" className="text-xs font-bold uppercase tracking-wider text-navy/70">Company *</Label>
                  <Input id="cf-company" data-testid="form-company-input" required value={form.company} onChange={set("company")} placeholder="Company name" className="h-11" />
                </div>
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="cf-email" className="text-xs font-bold uppercase tracking-wider text-navy/70">Work Email *</Label>
                  <Input id="cf-email" data-testid="form-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@company.com" className="h-11" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cf-phone" className="text-xs font-bold uppercase tracking-wider text-navy/70">Phone <span className="font-medium normal-case text-navy/40">(optional)</span></Label>
                  <Input id="cf-phone" data-testid="form-phone-input" type="tel" value={form.phone} onChange={set("phone")} placeholder="+91 ..." className="h-11" />
                </div>
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-wider text-navy/70">Company Stage / Type *</Label>
                  <Select required value={form.stage} onValueChange={set("stage")}>
                    <SelectTrigger data-testid="form-stage-select" className="h-11">
                      <SelectValue placeholder="Select stage" />
                    </SelectTrigger>
                    <SelectContent>
                      {STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-wider text-navy/70">What can we help with? *</Label>
                  <Select required value={form.topic} onValueChange={set("topic")}>
                    <SelectTrigger data-testid="form-topic-select" className="h-11">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {TOPICS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cf-message" className="text-xs font-bold uppercase tracking-wider text-navy/70">Message *</Label>
                <Textarea
                  id="cf-message"
                  data-testid="form-message-input"
                  required
                  minLength={5}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Briefly describe your priorities — e.g. fractional CFO support, finance setup in India, controllership, FP&A..."
                  rows={4}
                />
              </div>

              {status === "error" && (
                <p data-testid="form-error-message" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </p>
              )}

              <p className="text-xs leading-relaxed text-navy/50">
                By submitting this form, you agree that Cogniti.ai CFO may use the
                information provided to respond to your enquiry. Please see our{" "}
                <a
                  href="/privacy-policy"
                  data-testid="form-privacy-link"
                  className="font-semibold text-brand-hover underline underline-offset-2"
                >
                  Privacy Policy
                </a>.
              </p>

              <button
                data-testid="form-submit-btn"
                type="submit"
                disabled={status === "submitting"}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-brand py-4 text-sm font-bold text-navy shadow-[0_8px_24px_rgba(245,166,35,0.35)] transition-[transform,background-color,color] duration-300 hover:bg-brand-hover hover:text-white hover:scale-[1.01] disabled:opacity-60"
              >
                {status === "submitting" ? (
                  <><Loader2 size={16} className="animate-spin" /> Sending...</>
                ) : (
                  "Request Consultation"
                )}
              </button>
            </form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
